import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import { UrlUtils } from '../../shared/utils/url.utils';

import {
    Facet,
    FacetValue,
    ListDataParameters,
    PageListData,
    SelectedFacet,
} from '../../shared/interfaces/list-data-params';
import { ProjectDetails } from '../../shared/interfaces/project.interface';
import { SystemDateFormatService } from '@exl-ng/mulo-core';
import { MuloNavLink } from '@exl-ng/mulo-common';
import { JwtUtilService } from '../../core/jwt-util.service';
import { ProfileService } from '../profile.service';
import { Profile } from '../../shared/configurations/profile.constant';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ProfileProjectsService {
    private http = inject(HttpClient);
    private systemDateFormatService = inject(SystemDateFormatService);
    private jwtUtilService = inject(JwtUtilService);
    private profileService = inject(ProfileService);
    private translate = inject(TranslateService);

    institutionCode: string;
    selectedIndex$ = new BehaviorSubject<number>(0);

    facets: FacetValue[] = [];
    facetsAllProjects: string[] = [];
    facetPendingApprovalKey = 'project.pending_approval';
    facetsChange$ = new Subject<void>();
    facetsForMobile: MuloNavLink[] = [];

    allProjectsFacetsCount: number;

    constructor() {
        this.institutionCode = UrlUtils.getParam('institution');
    }

    getListDataParameters(
        newFacets?: { [key: string]: SelectedFacet[] },
        offset?: number,
    ) {
        const defaultListDataParams: ListDataParameters = {
            pagination: { rows: Profile.BULK_SIZE, offset: offset + 1 || 1 },
            facets: newFacets,
            filters: {
                profile_visibility: this.profileService.isPublicPage ? 'true' : null
            },
            search: {
                searchBy: 'member_researchers',
                searchText: this.jwtUtilService.getUserID(),
            },
            sorts: [{ sortBy: 'start_date', sortDir: 'desc' }],
        };
        return defaultListDataParams;
    }

    displayProjectsTabs() {
        let params = new HttpParams()
            .set('isUserLoggedIn', String(this.jwtUtilService.isLoggedIn()))
            .set('isPublicProfile', String(this.profileService.isPublicPage))
            .set('institution', this.institutionCode);
        let _facet = {};
        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
            _facet = _facet = this.getFacetForPublicPage();
        }
        return this.http.post(
            RestApiBase.projectApi.DISPLAY_PROJECTS_TAB,
            this.getListDataParameters(_facet),
            { params },
        );
    }

    getFacetsProjects() {
        let params = new HttpParams().set('institution', this.institutionCode);
        let _facet = {};
        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
            _facet = this.getFacetForPublicPage();
        }
        this.http
            .post(
                RestApiBase.projectApi.FACETS_PROJECTS,
                this.getListDataParameters(_facet),
                { params },
            )
            .subscribe((result: any) => {
                this.allProjectsFacetsCount = result.resultCount;
                this.setFacets(result);
                this.parseFacetsForMobile();
                this.facetsChange$.next();
            });
    }

    getAllProjects(facet: string, offset: number) {
        let params = new HttpParams().set('institution', this.institutionCode);
        let _facet = {
            repository_status: this.getRepositoryStatusByFacet(facet),
        };

        if (this.profileService.isPublicPage) {
            params = params.set('urlIdentifier', this.profileService.userID);
            _facet = this.getFacetForPublicPage();
        }

        return this.http.post<PageListData<ProjectDetails>>(
            RestApiBase.projectApi.ALL_PROJECTS + '/query',
            this.getListDataParameters(_facet, offset),
            { params },
        );
    }

    deleteProject(project: ProjectDetails) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const projectId = project.id;
        return this.http.delete(
            RestApiBase.projectApi.DELETE_PROJECT + '/' + projectId,
            { params },
        );
    }

    setFacets(result) {
        this.facetsAllProjects = [];
        result?.facets
            .filter((facet: any) => facet.key === 'repository_status')
            .map((facet: Facet) => {
                const newFacets: FacetValue[] = [];
                const allProjectsFacets: FacetValue = {
                    value: 'all_projects',
                    count: 0,
                    label: 'all.projects',
                };
                facet.facetValues?.forEach((facetValue) => {
                    if (facetValue.value !== this.facetPendingApprovalKey) {
                        allProjectsFacets.count += Number(facetValue.count);
                        this.facetsAllProjects.push(facetValue.value);
                    } else {
                        facetValue.label = 'in.process';
                        newFacets.push(facetValue);
                    }
                });
                newFacets.push(allProjectsFacets);
                this.facets = newFacets;
            });
    }

    getFacetForPublicPage() {
        return {
            repository_status: [
                this.getSelectedFacet('project.approved'),
                this.getSelectedFacet('project.approved_profiles'),
                this.getSelectedFacet('project.approved_portal'),
            ],
        };
    }

    getRepositoryStatusByFacet(facet: string) {
        return facet === this.facetPendingApprovalKey
            ? [this.getSelectedFacet(this.facetPendingApprovalKey)]
            : this.facetsAllProjects.map((item) => ({
                  key: item,
                  isInclude: true,
              }));
    }

    getSelectedFacet(_key: string) {
        const facet: SelectedFacet = {
            key: _key,
            isInclude: true,
        };
        return facet;
    }

    parseFacetsForMobile() {
        if (this.facets) {
            this.facetsForMobile = [];
            this.facets.forEach((facet: FacetValue) =>
                this.facetsForMobile.push(
                    this.parseFacetForMobileElem(facet, 0),
                ),
            );
        }
    }

    parseFacetForMobileElem(facet: FacetValue, level) {
        const muloFacetResult = {} as MuloNavLink;
        muloFacetResult.name = facet.value;
        this.translate
            .get('research.projects.facet.' + facet.label)
            .subscribe((res) => {
                muloFacetResult.label = res;
            });
        muloFacetResult.length = facet.count;
        muloFacetResult.level = level;
        muloFacetResult.id = this.facets.findIndex(
            (_facet) => _facet.value === facet.value,
        );
        return muloFacetResult;
    }

    getDateLabel(date: string) {
        return this.systemDateFormatService.getDateLabelFromPartial(date);
    }
}
